import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Copy, Check, ArrowUpRight } from 'lucide-react';
import { profileData } from '../data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission (mock)
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setSubmitStatus(null), 3000);
    }, 1000);
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(profileData.email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  return (
    <section id="contact" className="py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00ff88]/[0.02] to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-[#00ff88] text-sm font-medium uppercase tracking-wider mb-4 block">
            Get In Touch
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Let's Build <span className="gradient-text">Something</span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            Ready to discuss opportunities or collaborate on security projects.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            {/* Email Card */}
            <div
              onClick={copyEmail}
              className="bento-card p-6 cursor-pointer group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00ff88]/10 flex items-center justify-center group-hover:bg-[#00ff88]/20 transition-colors">
                    <Mail size={20} className="text-[#00ff88]" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Email</p>
                    <p className="text-white group-hover:text-[#00ff88] transition-colors">
                      {profileData.email}
                    </p>
                  </div>
                </div>
                {copiedEmail ? (
                  <Check size={20} className="text-[#00ff88]" />
                ) : (
                  <Copy size={20} className="text-white/30 group-hover:text-[#00ff88] transition-colors" />
                )}
              </div>
              <p className="text-white/30 text-xs mt-3">Click to copy email</p>
            </div>

            {/* Phone Card */}
            <a href={`tel:${profileData.phone}`} className="bento-card p-6 block group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00ff88]/10 flex items-center justify-center group-hover:bg-[#00ff88]/20 transition-colors">
                  <Phone size={20} className="text-[#00ff88]" />
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Phone</p>
                  <p className="text-white group-hover:text-[#00ff88] transition-colors">
                    {profileData.phone}
                  </p>
                </div>
              </div>
            </a>

            {/* Location Card */}
            <div className="bento-card p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00ff88]/10 flex items-center justify-center">
                  <MapPin size={20} className="text-[#00ff88]" />
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Location</p>
                  <p className="text-white">{profileData.location}</p>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <div className="bento-card p-6 border-[#00ff88]/20">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-3 h-3 bg-[#00ff88] rounded-full animate-pulse" />
                <span className="text-[#00ff88] text-sm font-medium">
                  Available for new opportunities
                </span>
              </div>
              <p className="text-white/50 text-sm">
                Open to full-time positions, contract work, and consulting opportunities in cybersecurity and IT infrastructure.
              </p>
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bento-card p-8"
          >
            <h3 className="font-display text-xl font-semibold text-white mb-6">
              Send a Message
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-white/40 text-xs uppercase tracking-wider mb-2 block">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#00ff88]/50 focus:outline-none focus:ring-2 focus:ring-[#00ff88]/20 transition-all"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="text-white/40 text-xs uppercase tracking-wider mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#00ff88]/50 focus:outline-none focus:ring-2 focus:ring-[#00ff88]/20 transition-all"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="text-white/40 text-xs uppercase tracking-wider mb-2 block">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#00ff88]/50 focus:outline-none focus:ring-2 focus:ring-[#00ff88]/20 transition-all resize-none"
                  placeholder="Tell me about your project or opportunity..."
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send size={16} />
              </motion.button>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-[#00ff88]/10 border border-[#00ff88]/30 rounded-xl text-center"
                >
                  <span className="text-[#00ff88] text-sm">
                    Message sent successfully! I'll get back to you soon.
                  </span>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
